import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Select } from '@chapche/ui';
import {
    ProductSvgAsset,
    ProductSvgLink,
    SvgAssetListItem,
    svgAssetsService,
    svgPreviewDataUrl,
} from '../../services/svgAssetsService';
import { errorCallback } from '../../util';

type OwnerKind = 'product' | 'recipe';

type Props = {
    ownerKind: OwnerKind;
    ownerId: string;
};

const toLinks = (items: ProductSvgAsset[]): ProductSvgLink[] =>
    items.map((item, index) => ({
        svgAssetId: item.svgAssetId,
        sortOrder: index,
        isPrimary: item.isPrimary,
        role: item.role ?? null,
    }));

const EntitySvgAssetsBlock = ({ ownerKind, ownerId }: Props) => {
    const { t } = useTranslation();
    const [items, setItems] = useState<ProductSvgAsset[]>([]);
    const [library, setLibrary] = useState<SvgAssetListItem[]>([]);
    const [pickId, setPickId] = useState('');
    const [loading, setLoading] = useState(true);

    const i18nPrefix = ownerKind === 'product' ? 'app.page.product.edit.svg' : 'app.page.recipe.edit.svg';

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [attached, all] = await Promise.all([
                ownerKind === 'product'
                    ? svgAssetsService.listForProduct(ownerId)
                    : svgAssetsService.listForRecipe(ownerId),
                svgAssetsService.list(),
            ]);
            setItems(attached);
            setLibrary(all);
        } catch (e) {
            errorCallback(e);
        } finally {
            setLoading(false);
        }
    }, [ownerId, ownerKind]);

    useEffect(() => {
        void reload();
    }, [reload]);

    const persist = async (next: ProductSvgAsset[]) => {
        const links = toLinks(next);
        if (links.length > 0 && !links.some((l) => l.isPrimary)) {
            links[0].isPrimary = true;
        }
        try {
            const saved =
                ownerKind === 'product'
                    ? await svgAssetsService.replaceForProduct(ownerId, links)
                    : await svgAssetsService.replaceForRecipe(ownerId, links);
            setItems(saved);
        } catch (e) {
            errorCallback(e);
            await reload();
        }
    };

    const attachedIds = new Set(items.map((i) => i.svgAssetId));
    const pickOptions = library
        .filter((a) => !attachedIds.has(a.id))
        .map((a) => ({ value: a.id, label: a.title }));

    const onAttach = async () => {
        if (!pickId) return;
        try {
            const link = {
                svgAssetId: pickId,
                sortOrder: items.length,
                isPrimary: items.length === 0,
            };
            const saved =
                ownerKind === 'product'
                    ? await svgAssetsService.attachToProduct(ownerId, link)
                    : await svgAssetsService.attachToRecipe(ownerId, link);
            setItems(saved);
            setPickId('');
        } catch (e) {
            errorCallback(e);
        }
    };

    const move = async (index: number, delta: number) => {
        const next = [...items];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        const tmp = next[index];
        next[index] = next[target];
        next[target] = tmp;
        await persist(next);
    };

    const setPrimary = async (assetId: string) => {
        await persist(
            items.map((item) => ({
                ...item,
                isPrimary: item.svgAssetId === assetId,
            })),
        );
    };

    const detach = async (assetId: string) => {
        try {
            if (ownerKind === 'product') {
                await svgAssetsService.detachFromProduct(ownerId, assetId);
            } else {
                await svgAssetsService.detachFromRecipe(ownerId, assetId);
            }
            await reload();
        } catch (e) {
            errorCallback(e);
        }
    };

    return (
        <div className="mt-24 flex flex-col gap-12">
            <h3 className="block__title-h3">{t(`${i18nPrefix}.title`)}</h3>
            <p className="block__content">{t(`${i18nPrefix}.hint`)}</p>
            <div className="flex items-center gap-12">
                <Select
                    fullWidth
                    className="mt-0"
                    options={pickOptions}
                    value={pickId}
                    placeholder={t(`${i18nPrefix}.pick`)}
                    onChange={(value) => setPickId(String(value))}
                />
                <Button onClick={onAttach} disabled={!pickId}>
                    {t(`${i18nPrefix}.add`)}
                </Button>
            </div>
            {!loading && items.length === 0 ? (
                <p className="block__content">{t(`${i18nPrefix}.empty`)}</p>
            ) : null}
            <ul className="m-0 flex list-none flex-col gap-10 p-0">
                {items.map((item, index) => (
                    <li key={item.svgAssetId} className="grid grid-cols-[72px_1fr_auto] items-center gap-12 border-b border-solid border-border py-8">
                        <img
                            className="h-[72px] w-[72px] object-contain bg-light-beige"
                            src={svgPreviewDataUrl(item.svgText)}
                            alt={item.title}
                        />
                        <div>
                            <strong>{item.title}</strong>
                            {item.isPrimary ? (
                                <div className="block__content">{t(`${i18nPrefix}.primary`)}</div>
                            ) : null}
                        </div>
                        <div className="flex flex-wrap justify-end gap-8">
                            <Button size="small" variant="outline" onClick={() => move(index, -1)} disabled={index === 0}>
                                {t(`${i18nPrefix}.up`)}
                            </Button>
                            <Button
                                size="small"
                                variant="outline"
                                onClick={() => move(index, 1)}
                                disabled={index === items.length - 1}
                            >
                                {t(`${i18nPrefix}.down`)}
                            </Button>
                            {!item.isPrimary ? (
                                <Button size="small" variant="outline" onClick={() => setPrimary(item.svgAssetId)}>
                                    {t(`${i18nPrefix}.setPrimary`)}
                                </Button>
                            ) : null}
                            <Link to={`/svg-library/${item.svgAssetId}/edit`}>
                                <Button size="small" variant="outline">{t(`${i18nPrefix}.openDraw`)}</Button>
                            </Link>
                            <Button size="small" variant="outline" onClick={() => detach(item.svgAssetId)}>
                                {t(`${i18nPrefix}.detach`)}
                            </Button>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default EntitySvgAssetsBlock;
